import { useCallback, useEffect, useRef, useState } from "react";
import investorStrategyService, {
  type InvestorStrategyRecord,
} from "../services/investorStrategyService";

export interface UseUserStrategyRecordsResult {
  strategyRecords: InvestorStrategyRecord[];
  /** Count of MANAGED_STRATEGY entries. Derived from entity type only. */
  managedCount: number;
  isLoading: boolean;
  hasError: boolean;
  /** Re-runs the fetch for the current user. No-op while no user is selected. */
  retry: () => void;
}

const EMPTY_RECORDS: InvestorStrategyRecord[] = [];

/**
 * Loads a user's unfiltered investor strategy records. A managed strategy is a
 * held product like any other, so the widgets fed by this hook must count it;
 * the self-directed lookup would hide it.
 * Shared by the admin users sidebar and the client onboarding modal.
 */
export function useUserStrategyRecords(
  userId: string | null
): UseUserStrategyRecordsResult {
  const [reloadToken, setReloadToken] = useState(0);
  const loadKey = userId === null ? null : `${userId}|${reloadToken}`;

  const [state, setState] = useState<{
    key: string | null;
    strategyRecords: InvestorStrategyRecord[];
    isLoading: boolean;
    hasError: boolean;
  }>({ key: null, strategyRecords: EMPTY_RECORDS, isLoading: false, hasError: false });

  // Monotonic token: this hook is keyed to a user-changeable selection (the
  // admin picks a row), so a slower earlier request must never write over a
  // newer one -- on the success path, the failure path, or the loading reset.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    if (userId === null || loadKey === null) {
      setState({
        key: null,
        strategyRecords: EMPTY_RECORDS,
        isLoading: false,
        hasError: false,
      });
      return;
    }

    setState({
      key: loadKey,
      strategyRecords: EMPTY_RECORDS,
      isLoading: true,
      hasError: false,
    });

    void investorStrategyService
      .getStrategyRecordsForUser(userId)
      .then((loadedRecords) => {
        if (requestId !== requestIdRef.current) return;
        setState({
          key: loadKey,
          strategyRecords: loadedRecords,
          isLoading: false,
          hasError: false,
        });
      })
      .catch((strategiesError: unknown) => {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to fetch strategies:", strategiesError);
        setState({
          key: loadKey,
          strategyRecords: EMPTY_RECORDS,
          isLoading: false,
          hasError: true,
        });
      });
  }, [loadKey, userId]);

  const retry = useCallback(() => {
    setReloadToken((currentReloadToken) => currentReloadToken + 1);
  }, []);

  // Until this render's key has been committed, report the pending state rather
  // than a previous selection's resolved records.
  if (state.key !== loadKey) {
    return {
      strategyRecords: EMPTY_RECORDS,
      managedCount: 0,
      isLoading: loadKey !== null,
      hasError: false,
      retry,
    };
  }

  return {
    strategyRecords: state.strategyRecords,
    managedCount: state.strategyRecords.filter(
      (strategyRecord) => strategyRecord.entityType === "MANAGED_STRATEGY"
    ).length,
    isLoading: state.isLoading,
    hasError: state.hasError,
    retry,
  };
}
